import React, { useState } from "react";
import { Box, Button, Grid, IconButton, Stack } from "@mui/material";
import { Helmet } from "react-helmet";
import { useMutation, useQuery } from "react-query";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { LoadingButton } from "@mui/lab";
import { DriveFileRenameOutline } from "@mui/icons-material";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import CustomLoader from "app/components/common/CustomLoader";
import { selectActiveSession } from "app/modules/schools/selectors";
import { CustomTextField } from "assets/styles/globalStyledComponent";
import { getSessionSerialNumber, updateSessionSerialNumber } from "app/services/schoolService/schoolManagement.service";

const fieldConfig = [
  { key: "serialNoFG", label: "Serial No FG", type: "number" },
  { key: "FeeInvoiceCount", label: "Fee Invoice Count", type: "number" },
  {
    key: "InvoiceSerialNumber",
    label: "Invoice Serial Number",
    type: "number",
  },
  {
    key: "BookInvoiceSerialNumber",
    label: "Book Invoice Serial Number",
    type: "number",
  },
  {
    key: "ExpenseSerialNumber",
    label: "Expense Serial Number",
    type: "number",
  },
  {
    key: "NoticeSerialNumber",
    label: "Notice Serial Number",
    type: "number",
  },
  {
    key: "SalaryPaySerialNumber",
    label: "Salary Pay Serial Number",
    type: "number",
  },
  {
    key: "ComplainSerialNumber",
    label: "Complain Serial Number",
    type: "number",
  },
  {
    key: "SalarySerialNumber",
    label: "Salary Serial Number",
    type: "number",
  },
  {
    key: "ArrearInvoiceCount",
    label: "Arrear Invoice Count",
    type: "number",
  },
  {
    key: "AdmissionEnqCount",
    label: "Admission Enquiry Count",
    type: "number",
  },
  {
    key: "AdmissionFormCount",
    label: "Admission Form Count",
    type: "number",
  },
];

const getInitialState = () =>
  fieldConfig.reduce((acc, field) => {
    acc[field.key] = "";
    return acc;
  }, {});

const SessionDependent = () => {
  const selectedSession = useSelector(selectActiveSession);
  const [state, setState] = useState({
    ...getInitialState(),
    isChanged: false,
  });
  const [initialData, setInitialData] = useState(getInitialState());
  const [editingField, setEditingField] = useState(null);

  const {
    isLoading: listLoading,
    isFetching: listFetching,
    refetch,
  } = useQuery(
    ["getSessionSerialNumber", selectedSession],
    () => getSessionSerialNumber({ sessionName: selectedSession }),
    {
      onSuccess: (response) => {
        const apiData =
          response?.data?.obj || response?.data?.data || response?.data || {};
        const normalizedData = fieldConfig.reduce((acc, field) => {
          const value = apiData?.[field.key];
          acc[field.key] =
            value === undefined || value === null ? "" : String(value);
          return acc;
        }, {});

        setState({ ...normalizedData, isChanged: false });
        setInitialData(normalizedData);
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ||
            "Failed to load session serial numbers",
        );
      },
      enabled: !!selectedSession,
    },
  );

  const { mutate: updateSerialNumbers, isLoading: saveLoading } = useMutation(
    updateSessionSerialNumber,
    {
      onSuccess: (response) => {
        toast.success(
          response?.data?.message ||
            "Session serial numbers updated successfully",
        );
        refetch();
      },
      onError: (error) => {
        toast.error(
          error?.response?.data?.message ||
            "Failed to update session serial numbers",
        );
      },
    },
  );

  const handleChange = (field, value) => {
    setState((prev) => {
      const updatedState = { ...prev, [field]: value };
      const hasChanged = fieldConfig.some(
        (item) => updatedState[item.key] !== initialData[item.key],
      );
      return { ...updatedState, isChanged: hasChanged };
    });
  };

  const validatePayload = () => {
    for (const field of fieldConfig) {
      if (field.type !== "number") {
        continue;
      }

      const value = state[field.key];
      if (value === "") {
        toast.error(`${field.label} is required`);
        return false;
      }

      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) {
        toast.error(`${field.label} must be a valid number`);
        return false;
      }
    }

    return true;
  };

  const handleSave = () => {
    if (!validatePayload()) {
      return;
    }

    const payload = fieldConfig.reduce((acc, field) => {
      if (field.type === "number") {
        acc[field.key] = Number(state[field.key]);
      } else {
        acc[field.key] = state[field.key]?.trim();
      }
      return acc;
    }, {});

    payload.session = selectedSession;

    updateSerialNumbers(payload);
  };

  const handleClear = () => {
    setState({ ...initialData, isChanged: false });
    setEditingField(null);
  };

  return (
    <>
      <Helmet>
        <title>Session Dependent Serial Number</title>
      </Helmet>

      <CustomLoader show={listLoading || listFetching} />

      <Box sx={{ p: 3 }}>
        <Grid container spacing={2.5}>
          {fieldConfig.map((field) => (
            <Grid item xs={12} sm={6} md={4} key={field.key}>
              <TextFieldLabel title={field.label} required />
              <Stack direction="row" alignItems="center" gap={1}>
                <CustomTextField
                  fullWidth
                  size="small"
                  type={field.type}
                  value={state[field.key]}
                  onChange={(e) => handleChange(field.key, e.target.value)}
                  inputProps={field.type === "number" ? { min: 0 } : undefined}
                  disabled={editingField !== field.key}
                />
                <IconButton
                  color="secondary"
                  onClick={() =>
                    setEditingField((prev) =>
                      prev === field.key ? null : field.key
                    )
                  }
                  size="small"
                  sx={{
                    backgroundColor:
                      editingField === field.key ? "#0C2F49" : "#E3E9EF",
                    "&:hover": {
                      backgroundColor:
                        editingField === field.key ? "#0C2F49" : "#E3E9EF",
                    },
                    color: editingField === field.key ? "#FFFFFF" : "#0C2F49",
                  }}
                >
                  <DriveFileRenameOutline fontSize="medium" />
                </IconButton>
              </Stack>
            </Grid>
          ))}

          <Grid item xs={12}>
            <Stack direction="row" gap={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                color="secondary"
                size="large"
                onClick={handleClear}
                disabled={!state.isChanged || saveLoading}
              >
                Clear
              </Button>
              <LoadingButton
                variant="contained"
                color="secondary"
                size="large"
                onClick={handleSave}
                disabled={!state.isChanged}
                loading={saveLoading}
              >
                Save
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default SessionDependent;
